import type { Player } from '@/types'
import type { HashAlignment } from '@/types'
import { extractFormationPositions } from '@/components/canvas/FormationCanvas'
import { getDefaultColorForRole } from '@/constants/Formation.constants'

/** Input type for creating/updating formation positions (block-based API) */
export interface FormationPositionInput {
	role: string
	x: number
	y: number
	color?: string
	isLineman?: boolean
	hashRelative?: boolean
	hashMark?: string | null
}

/** Hash positions structure for all three hash levels */
export interface HashPositions {
	left: Player[]
	middle: Player[]
	right: Player[]
}

/** Input type for creating/updating a formation (block-based API) */
export interface FormationInput {
	name: string
	description?: string
	thumbnail?: string | null
	hashMode?: boolean
	positions: FormationPositionInput[]
}

/** Input type for creating/updating a linked formation pair */
export interface LinkedFormationPairInput {
	formationA: FormationInput
	formationB: FormationInput
}

/**
 * Maps a hash alignment to its mirror counterpart.
 * Used for bidirectional sync between primary and mirrored formations.
 *
 * Mapping:
 * - left ↔ right
 * - middle ↔ middle
 *
 * @param hash - The hash alignment to mirror
 * @returns The mirrored hash alignment
 */
export function getMirrorHash(hash: HashAlignment): HashAlignment {
	switch (hash) {
		case 'left':
			return 'right'
		case 'right':
			return 'left'
		case 'middle':
		default:
			return 'middle'
	}
}

/**
 * Collects positions from all hash levels into a flat array for API submission.
 * Each position is tagged with its hash_mark for server-side grouping.
 *
 * @param hashPositions - Object containing player arrays for each hash level
 * @returns Flat array of all positions with hash_mark metadata
 */
export function collectHashPositions(hashPositions: HashPositions): FormationPositionInput[] {
	const allPositions: FormationPositionInput[] = []

	for (const hash of ['left', 'middle', 'right'] as const) {
		const hashPlayers = hashPositions[hash]
		const positions = extractFormationPositions(hashPlayers)

		positions.forEach((pos) => {
			allPositions.push({
				role: pos.label || '',
				x: pos.x,
				y: pos.y,
				color: pos.color || getDefaultColorForRole(pos.label || ''),
				hashRelative: true,
				hashMark: hash,
			})
		})
	}

	return allPositions
}
